<script
    setup
    lang="ts"
    generic="Range extends 'hidden' | 'disabled' | 'enabled' = 'hidden'"
>
// @TODO close vs btn-close event has ambiguity
import type { InputValue, Page, RangeValue } from 'v-customizable-date-picker';
import {
    faCalendar,
    faPlaneArrival,
    faPlaneDeparture,
    faPlus,
    faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import DatepickerHelpers from './DatepickerHelpers.vue';
import DatepickerCalendar from './DatepickerCalendar.vue';
import type { DayConfig } from './types';
import { type LabeledPriceObject } from '@search/ItineraryPriceLoader';
import { CalendarType } from '@utils/itinerary';
import { type DateFormat, dayConfig, type InputSizeType } from '#imports';

const {
    countNights,
    includeYear,
    clearable,
    direction = 'rtl',
    disabled,
    loadingPrice,
    canChangeRange,
    hideLabel,
    doubleMonth,
    format = 'long',
    includeDayName,
    endDayTitle = 'برگشت',
    startDayTitle = 'رفت',
    invalid,
    maxDate,
    minDate,
    prependTitle,
    joined,
    prices = [],
    modalZIndex = 1000010,
    readonly,
    hideIcon,
    hideInputs,
    size = InputSize.dense as InputSizeType,
    desktopInRowInputs,
    futureDisabled,
    pastEnabled,
} = defineProps<IProps>();
const emit = defineEmits<{
    open: [];
    close: [];
    done: [];
    pageChange: [date: Date];
    btnClose: [];
    back: [];
    submit: [];
}>();
const infiniteScrollContainer = ref<HTMLDivElement>();
const wrapper = ref<HTMLDivElement>();
const calendarWrapper = ref<HTMLDivElement>();
const datePickerCalendarRef = ref<InstanceType<typeof DatepickerCalendar>>();
onClickOutside(wrapper, () => {
    if (open.value && !nowShown.value) {
        hide(true);
    }
});
interface IProps {
    includeDayName?: boolean;
    includeYear?: boolean;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    format?: DateFormat;
    prices?: LabeledPriceObject[];
    futureDisabled?: boolean;
    pastEnabled?: boolean;
    doubleMonth?: boolean;
    invalid?: boolean;
    hideIcon?: boolean;
    unequalRange?: boolean;
    size?: InputSizeType;
    hideInputs?: boolean;
    direction?: 'rtl' | 'ltr';
    countNights?: boolean;
    hideLabel?: boolean;
    hideDescription?: boolean;
    joined?: boolean;
    desktopInRowInputs?: boolean;
    endDayTitle?: string;
    startDayTitle?: string;
    prependTitle?: boolean;
    canChangeRange?: boolean;
    modalZIndex?: number;
    readonly?: boolean;
    clearable?: boolean;
    loadingPrice?: boolean;
    description?: string;
}
const modelValue = defineModel<
    (Range extends 'hidden' ? Date : RangeValue) | null
>({ default: null });
const range = defineModel<Range>('range', { default: () => 'hidden' as Range });
const open = defineModel<boolean>('open', { default: false });
const { isMobile } = useSize();
const nowShown = ref(false);
const focusToNext = ref(true);
const dragging = ref(false);
const maxMonthCount = ref(2);
const router = useRouter();
const localValue = computed({
    get(): InputValue {
        if (isRangeEnabled.value) {
            return rangeDate.value;
        } else if (isRangeShowing.value) {
            return rangeDate.value.start;
        } else {
            return date.value;
        }
    },
    set(value: InputValue) {
        const output =
            range.value === 'hidden'
                ? (value as Date)
                : range.value === 'disabled'
                  ? { start: value as Date, end: null }
                  : (value as RangeValue);
        modelValue.value = output as any; // not important inside, interface is more important
        dragging.value = false;
    },
});
const isRangeEnabled = computed(() => range.value === 'enabled');
const isRangeShowing = computed(() => range.value !== 'hidden');
const date = computed(() => {
    if (!isRangeShowing.value && modelValue.value) {
        return modelValue.value as Date;
    }
    return null;
});
const rangeDate = computed<RangeValue>(() => {
    if (isRangeShowing.value && modelValue.value) {
        return {
            start: (modelValue.value as RangeValue).start,
            end: (modelValue.value as RangeValue).end,
        };
    }
    return {
        start: null,
        end: null,
    };
});
const startDateString = computed(() => {
    const lDate = formatDate(rangeDate.value.start || date.value!);
    if (disabled || (!date.value && !rangeDate.value.start)) {
        return '';
    }
    if (!prependTitle) {
        return lDate;
    }
    return `${startDayTitle}: ${date}`;
});
const endDateString = computed(() => {
    if (disabled || range.value !== 'enabled' || !rangeDate.value.end) {
        return '';
    }
    const date = formatDate(rangeDate.value.end!);
    if (!prependTitle) {
        return date;
    }
    return `${endDayTitle}: ${date}`;
});
const currentCalendar = computed(() => {
    switch (dayConfig.calendar) {
        case CalendarType.Gregorian:
            return 1;
        case CalendarType.Jalali:
        default:
            return 0;
    }
});
const calendar = computed(() =>
    currentCalendar.value ? gregorianCalendar : jalaliCalendar
);
const monthCount = computed(() => {
    if (isMobile.value) {
        return 2;
    }
    return doubleMonth ? 2 : 1;
});
// WTF
const dayPriceInfo = computed(() => getPriceInfo());
const nights = computed(() => {
    if (!rangeDate.value.start || !rangeDate.value.end) {
        return '';
    }
    return `${dayConfig
        .gregory(rangeDate.value.end)
        .startOf('day')
        .diff(
            dayConfig.gregory(rangeDate.value.start).startOf('day'),
            'day'
        )} شب اقامت`;
});
const pickingEnd = computed(() => range.value !== 'hidden' && !dragging.value);
const headerTitle = computed(() => {
    if (!isRangeEnabled.value) {
        return `انتخاب تاریخ ${startDayTitle.replaceAll('تاریخ', '')}`;
    }
    if ((modelValue.value as RangeValue)?.end) {
        return `انتخاب تاریخ ${startDayTitle.replaceAll('تاریخ', '')}`;
    } else {
        return `انتخاب تاریخ ${endDayTitle.replaceAll('تاریخ', '')}`;
    }
});
const endDayRequired = computed(() => isRangeEnabled.value);
const isEndFocused = computed(
    () =>
        isRangeEnabled.value &&
        !!localValue.value &&
        !(localValue.value as RangeValue).end
);
const getPriceInfo = () => {
    const info = { configs: {} as Record<string, DayConfig> };
    if (!prices) {
        return info;
    }
    prices.forEach(item => {
        const price = item.value;
        const { priceStatus, bundle } = item;
        const key = item.date;
        info.configs[key] = {
            label:
                price === '-' || price === null
                    ? '-'
                    : `${formatMoney(price, {
                          includeUnit: false,
                          unit: 'KToman',
                      })} ت`,
        };
        if (priceStatus === 'min') {
            info.configs[key] = {
                ...info.configs[key],
                eventData: { isMin: true },
                color: { main: 'var(--green-700)' },
                labelColor: { main: 'var(--green-700)' },
                background: { main: 'var(--green-200)' },
            };
        }
        if (bundle) {
            info.configs[key] = {
                ...info.configs[key],
                icon: bundle === 'return' ? faPlaneArrival : faPlaneDeparture,
                iconColor: {
                    main: 'var(--cool-gray-600)',
                    hover: 'white',
                    selected: 'white',
                },
            };
        }
    });
    return info;
};
const close = () => {
    hide(false);
    dragging.value = false;
};
const submit = () => {
    hide(false, true);
    dragging.value = false;
    emit('submit');
};
const btnCloseClicked = () => {
    onBtnClose();
    close();
};
function btnBackClicked() {
    router.back();
    emit('back');
}
const endInputOnClick = () => {
    if (range.value !== 'enabled') {
        range.value = 'enabled' as Range;
    }
    nextTick(() => {
        (localValue.value as RangeValue) = {
            ...(localValue.value as RangeValue),
            end: null,
        };
        show();
    });
};
const rangeChanged = () => {
    focusToNext.value = false;
    nextTick(() => {
        focusToNext.value = true;
    });
};
const show = () => {
    if (disabled) {
        return;
    }
    open.value = true;
    nowShown.value = true;
    nextTick(() => {
        setTimeout(() => {
            nowShown.value = false;
        }, 150);
    });
    if (isMobile.value) {
        document.documentElement.style.overflow = 'hidden';
    }
    nextTick(() => scrollOnDatePicker());
};

const scrollOnDatePicker = () => {
    if (isMobile.value) {
        return;
    }
    setTimeout(() => {
        calendarWrapper.value?.scrollIntoView({ block: 'nearest' });
    }, 300);
};

const hide = (onlyIfOnDesktop: boolean, done?: boolean) => {
    if (isMobile.value) {
        document.documentElement.style.overflow = 'auto';
    }
    if (onlyIfOnDesktop && isMobile.value) {
        return;
    }
    if (done) {
        onDone();
    }
    open.value = false;
};
const pageChange = (page: Page) => {
    let lastDate = calendar.value.getDate(page.year, page.month, 1);
    lastDate = dayConfig
        .js(lastDate)
        .add(1, 'month')
        .endOf('months')
        .add(1, 'day')
        .toDate();
    emit('pageChange', lastDate);
};
const focus = () => {
    if (disabled) {
        return false;
    }
    setTimeout(() => show());
    return true;
};
const formatDate = (date: Date) => {
    if (!date) {
        return '';
    }
    return dayConfig.js(date).format(
        formatter({
            format,
            includeWeekday: includeDayName as false,
            includeYear: includeYear as false,
        })
    );
};
const loadNextMonth = () => {
    if (futureDisabled) {
        return;
    }
    if (isMobile.value && maxMonthCount.value < 20) {
        maxMonthCount.value++;
        const newPage = datePickerCalendarRef.value?.appendMonth();
        pageChange(newPage!);
    }
};
const loadPrevMonth = () => {
    if (!pastEnabled) {
        return;
    }
    if (isMobile.value && maxMonthCount.value < 20) {
        const prevHeight = infiniteScrollContainer.value?.scrollHeight || 0;
        maxMonthCount.value++;
        const newPage = datePickerCalendarRef.value?.prependMonth();
        pageChange(newPage!);
        nextTick(() => {
            infiniteScrollContainer.value!.scrollTop =
                infiniteScrollContainer.value!.scrollHeight - prevHeight;
        });
    }
};
const onOpen = () => {
    emit('open');
    datePickerCalendarRef.value?.scrollSelectedToView();
};
const onDone = () => emit('done');
const onBtnClose = () => emit('btnClose');
watch(open, val => {
    if (val) {
        onOpen();
    } else {
        datePickerCalendarRef.value?.reset();
        emit('close');
    }
});
watch(range, () => rangeChanged());
onMounted(() => {
    useInfiniteScroll(infiniteScrollContainer, loadNextMonth, {
        distance: 300,
    });
    useInfiniteScroll(infiniteScrollContainer, loadPrevMonth, {
        direction: 'top',
    });
});
defineExpose({
    focus,
    hide,
});
</script>
<template>
    <div ref="wrapper" class="datepicker-input-wrapper">
        <div
            class="datepicker-inputs-container"
            :class="{ joined, 'desktop-in-row': desktopInRowInputs }"
        >
            <mr-input
                :model-value="startDateString"
                class="datepicker-input start"
                readonly
                :hide-description
                required
                :placeholder="startDayTitle"
                :label="(!hideLabel && startDayTitle) || undefined"
                :size
                :invalid
                :icon="!hideIcon ? faCalendar : undefined"
                :dir="direction"
                :flat-left="joined"
                :clearable
                :description
                @focus="show"
                @click="show"
                @clear="modelValue = null"
            />
            <mr-input
                v-if="isRangeShowing"
                class="datepicker-input end"
                :model-value="endDateString"
                readonly
                :hide-description
                :required="endDayRequired"
                :placeholder="endDayTitle"
                :label="(!hideLabel && endDayTitle) || undefined"
                :size
                :invalid
                :icon="!hideIcon && !joined ? faCalendar : undefined"
                :dir="direction"
                :flat-right="joined"
                :clearable="canChangeRange && isRangeEnabled"
                @click="endInputOnClick"
                @clear="range = 'disabled' as Range"
            />
        </div>
        <ClientOnly>
            <Transition v-if="!disabled">
                <div
                    v-show="open"
                    ref="calendarWrapper"
                    class="datepicker-modal-container"
                    :style="{ zIndex: modalZIndex }"
                    @click.stop
                >
                    <div
                        v-show="open"
                        class="marker"
                        :class="{
                            'end-focused': isEndFocused,
                        }"
                    />
                    <mr-modal-header
                        :title="headerTitle"
                        @close="btnCloseClicked"
                        @back="btnBackClicked"
                    >
                        <div
                            v-if="!hideInputs && isRangeShowing"
                            class="inputs-bar"
                        >
                            <mr-input
                                :model-value="startDateString"
                                class="mobile-input"
                                :class="{
                                    'is-active': !pickingEnd,
                                }"
                                required
                                readonly
                                :size
                                :placeholder="startDayTitle"
                                hide-description
                            />
                            <mr-input
                                :model-value="endDateString"
                                class="mobile-input"
                                :class="{
                                    'is-active': pickingEnd,
                                }"
                                readonly
                                :size
                                :placeholder="endDayTitle"
                                :icon="range !== 'enabled' ? faPlus : undefined"
                                hide-description
                                :required="endDayRequired"
                                @click="endInputOnClick"
                            >
                                <mr-button
                                    v-if="canChangeRange && isRangeEnabled"
                                    :icon-button="faXmark"
                                    size="big"
                                    variant="transparent"
                                    @click.stop.prevent="
                                        range = 'disabled' as Range
                                    "
                                />
                            </mr-input>
                        </div>
                        <DatepickerHelpers
                            :night-count-helper="countNights"
                            :nights
                        />
                    </mr-modal-header>
                    <ClientOnly>
                        <div
                            ref="infiniteScrollContainer"
                            class="mr-datepicker-container"
                        >
                            <DelayHydration>
                                <DatepickerCalendar
                                    ref="datePickerCalendarRef"
                                    v-model="localValue"
                                    class="mr-datepicker"
                                    :current-calendar
                                    :month-count
                                    :range="isRangeEnabled"
                                    :min="minDate"
                                    :max="maxDate"
                                    :loading="loadingPrice"
                                    :day-config="dayPriceInfo.configs"
                                    :range-start-day-tooltip="
                                        startDayTitle.replaceAll('تاریخ', '')
                                    "
                                    :range-end-day-tooltip="
                                        endDayTitle.replaceAll('تاریخ', '')
                                    "
                                    :read-only="readonly"
                                    :unequal-range
                                    @drag="() => (dragging = true)"
                                    @page-change="pageChange"
                                />
                            </DelayHydration>
                        </div>
                    </ClientOnly>
                    <div class="actions-bar-container">
                        <DatepickerHelpers
                            :night-count-helper="countNights"
                            :nights
                            desktop-only
                        />
                        <div class="actions">
                            <mr-button
                                class="datepicker-btn"
                                variant="bordered"
                                :size="{ desktop: 'medium', mobile: 'big' }"
                                @click="switchCalendar"
                            >
                                تبدیل به
                                {{
                                    dayConfig.calendar === 'jalali'
                                        ? 'میلادی'
                                        : 'شمسی'
                                }}
                            </mr-button>
                            <mr-button
                                class="datepicker-btn"
                                :size="{ desktop: 'medium', mobile: 'big' }"
                                @click="submit"
                            >
                                تأیید
                            </mr-button>
                        </div>
                    </div>
                </div>
            </Transition>
        </ClientOnly>
    </div>
</template>

<style lang="scss" scoped>
.datepicker-input-wrapper {
    position: relative;
    height: 100%;
}

.datepicker-inputs-container {
    position: relative;
    display: flex;
    flex-direction: column;
    &.desktop-in-row {
        flex-direction: row;
        .datepicker-input {
            margin-top: 0;
            &:first-child {
                margin-left: 8px;
            }
        }
    }
    &.joined {
        flex-direction: row;
        .datepicker-input {
            margin-top: 0;
        }
    }
}

.datepicker-modal-container {
    @include shadow;
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border-radius: 5px;
    padding: 12px;
    .marker {
        position: absolute;
        top: -12px;
        left: 65%;
        transform: translateX(-50%);
        z-index: 1;
        width: 0;
        height: 0;
        border-left: 26px solid transparent;
        border-right: 26px solid transparent;
        border-bottom: 18px solid white;
        &.end-focused {
            left: 40%;
        }
    }
}

.mr-datepicker-container {
    z-index: 10;
    position: relative;
}

.actions-bar-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 10px;
    padding-inline: 6px;
    .actions {
        display: flex;
        @include f-gap(10px);
        @include mobile-only {
            flex-grow: 1;
        }
        .datepicker-btn {
            width: 120px;
            @include mobile-only {
                width: 100%;
            }
        }
    }
}

/*Mobile phones*/
@media (max-width: $app-mobile-max-width) {
    @include v-transition-horizontal-paging;
    .datepicker-inputs-container {
        &.desktop-in-row {
            flex-direction: column;
            .datepicker-input {
                margin-top: 0;
                &:last-child {
                    margin-top: 8px;
                }
                &:first-child {
                    margin-left: 0;
                }
            }
        }
    }
    .datepicker-modal-container {
        position: fixed;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        margin-top: 0;
        overflow: hidden;
        background: white;
        display: flex;
        flex-direction: column;
        transform: unset;
        border-radius: unset;
        padding: 0;
        .marker {
            display: none;
        }
    }

    .inputs-bar {
        display: flex;
        padding: 8px;
        background-color: white;
        .mobile-input {
            margin: 0 4px;
        }
    }

    .mr-datepicker-container {
        overflow-y: auto;
        //noinspection CssOverwrittenProperties
        height: 100%;
        //noinspection CssOverwrittenProperties
        height: calc(100% - 76px);
    }

    .actions-bar-container {
        padding: 8px;
        z-index: 2;
        background-color: white;
        .datepicker-btn {
            margin: 0 4px;
        }
    }
}
</style>
